import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, RefreshCw, Send, FileText, ClipboardList } from 'lucide-react'
import toast from 'react-hot-toast'
import cd2bLevantamientoService from '@/services/cd2bLevantamientoService'
import cd2bPropuestaService from '@/services/cd2bPropuestaService'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import DataTable from '@/components/common/DataTable'
import { EmptyState } from '@/components/common/EmptyState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog'

const FORM_VACIO = { empresaNombre: '', clienteEmail: '', pais: '' }

const ESTADO = {
  borrador: { label: 'Borrador', variant: 'secondary' },
  enviado: { label: 'Enviado al cliente', variant: 'default' },
  respondido: { label: 'Respondido', variant: 'accent' },
  aplicado: { label: 'Ya cotizado', variant: 'success' },
}

const fecha = (d) => (d ? new Date(d).toLocaleDateString('es-SV', { day: 'numeric', month: 'short' }) : '—')

/**
 * Levantamientos: el cuestionario que precede a la cotización.
 *
 * Existe porque el paquete se elegía a ojo. Aquí se anota lo que de verdad decide el plan
 * —facturas al mes, usuarios, sucursales, áreas— y de ahí sale la cotización dimensionada.
 */
export default function ConsultorLevantamientos() {
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState(FORM_VACIO)
  const [creando, setCreando] = useState(false)

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const res = await cd2bLevantamientoService.getAll()
      setItems(res?.data || [])
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error al cargar los levantamientos')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { cargar() }, [cargar])

  // Países habilitados (sin roadmap), cargados al abrir el diálogo. Sin lista de respaldo.
  const [paises, setPaises] = useState(null)
  const abrirCrear = () => {
    setForm(FORM_VACIO)
    setFormOpen(true)
    if (paises === null) {
      cd2bPropuestaService.getPaises()
        .then((r) => setPaises((r?.data || []).filter((x) => x.estado !== 'roadmap')))
        .catch(() => setPaises([]))
    }
  }

  const crear = async (e) => {
    e.preventDefault()
    const empresaNombre = form.empresaNombre.trim()
    if (!empresaNombre) return toast.error('El nombre de la empresa es obligatorio')
    setCreando(true)
    try {
      const res = await cd2bLevantamientoService.create({
        empresaNombre,
        clienteEmail: form.clienteEmail.trim() || undefined,
        // Opcional aquí: se copia a la cotización, donde es obligatorio para crear la empresa.
        pais: form.pais || undefined,
      })
      setFormOpen(false)
      navigate(`/consultor/levantamientos/${res.data._id}`)
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudo crear')
    } finally {
      setCreando(false)
    }
  }

  const enviar = async (l) => {
    try {
      const res = await cd2bLevantamientoService.enviar(l._id)
      try { await navigator.clipboard.writeText(res.data.url) } catch { /* noop */ }
      toast.success('Cuestionario enviado al cliente (enlace copiado)')
      cargar()
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo enviar')
    }
  }

  const armar = async (l) => {
    try {
      const res = await cd2bLevantamientoService.armarCotizacion(l._id)
      toast.success(res.data.ya ? 'Esta cotización ya existía' : 'Cotización armada con las respuestas')
      navigate(`/consultor/cotizaciones/${res.data.propuestaId}`)
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo armar la cotización')
    }
  }

  /** La acción que toca según el estado: armar si ya hay respuestas, enviar si no; abrir siempre. */
  const acciones = (l) => {
    const sug = l.sugerencia
    const enviable = l.clienteEmail && l.estado !== 'aplicado'
    return (
      <div className="flex w-full items-center justify-end gap-1.5 md:w-auto" onClick={(e) => e.stopPropagation()}>
        {sug?.edicion ? (
          <Button size="xs" className="flex-1 md:flex-none" onClick={() => armar(l)}
            title={l.propuestaId ? 'Ver la cotización que salió de aquí' : 'Armar la cotización con estas respuestas'}>
            <FileText size={15} />{l.propuestaId ? 'Ver cotización' : 'Armar cotización'}
          </Button>
        ) : enviable ? (
          <Button size="xs" variant="outline" className="flex-1 md:flex-none" onClick={() => enviar(l)}
            title="Enviárselo al cliente para que lo responda">
            <Send size={15} />{l.estado === 'enviado' ? 'Reenviar' : 'Enviar'}
          </Button>
        ) : (
          <Button size="xs" variant="outline" className="flex-1 md:flex-none" onClick={() => navigate(`/consultor/levantamientos/${l._id}`)}>
            <ClipboardList size={15} />Abrir
          </Button>
        )}
        {sug?.edicion && enviable && (
          <Button variant="ghost" size="icon-sm" aria-label={`Enviar el cuestionario de ${l.empresaNombre} al cliente`}
            title="Enviárselo al cliente para que lo responda" onClick={() => enviar(l)}>
            <Send size={16} />
          </Button>
        )}
      </div>
    )
  }

  const columnas = [
    {
      clave: 'empresa', titulo: 'Empresa', prioridad: 'titulo',
      celda: (l) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-foreground">{l.empresaNombre}</p>
          <p className="truncate text-caption text-subtle">{l.clienteEmail || 'Sin correo de contacto'}</p>
        </div>
      ),
    },
    {
      clave: 'sugerencia', titulo: 'Sugerencia', prioridad: 'meta',
      celda: (l) => (l.sugerencia?.edicion
        ? <span>{l.sugerencia.edicion} · {(l.sugerencia.modulos || []).length} módulo(s)</span>
        : <span className="text-subtle">Sin responder</span>),
    },
    {
      clave: 'estado', titulo: 'Estado', prioridad: 'valor',
      celda: (l) => {
        const e = ESTADO[l.estado] || { label: l.estado, variant: 'secondary' }
        return <Badge variant={e.variant}>{e.label}</Badge>
      },
    },
    { clave: 'actualizado', titulo: 'Actualizado', prioridad: 'meta', celda: (l) => <span className="text-subtle">{fecha(l.updatedAt)}</span> },
    { clave: 'acciones', titulo: 'Acción', alineacion: 'derecha', prioridad: 'acciones', celda: acciones },
  ]

  return (
    <PageContainer>
      <PageHeader
        title="Levantamientos"
        description="Las preguntas que dimensionan la venta: cuánto factura, cuántos usuarios, cuántas sucursales."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={cargar} aria-label="Refrescar">
              <RefreshCw size={18} />
            </Button>
            <Button onClick={abrirCrear}><Plus size={16} /> <span className="hidden sm:inline">Nuevo levantamiento</span><span className="sm:hidden">Nuevo</span></Button>
          </div>
        }
      />

      <DataTable
        etiquetaTabla="Levantamientos"
        columnas={columnas}
        filas={items}
        claveDeFila={(l) => l._id}
        cargando={loading}
        tarjetasEnMovil
        onFilaClick={(l) => navigate(`/consultor/levantamientos/${l._id}`)}
        className="bg-card"
        vacio={
          <EmptyState
            icono={<ClipboardList size={24} />}
            titulo="Todavía no hay levantamientos"
            descripcion="Empieza uno antes de cotizar: el plan, los topes y los add-ons salen de estas respuestas."
            accion={<Button size="sm" onClick={abrirCrear}><Plus size={16} />Nuevo levantamiento</Button>}
          />
        }
      />

      <Dialog open={formOpen} onOpenChange={(open) => !creando && setFormOpen(open)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo levantamiento</DialogTitle>
            <DialogDescription>
              Empieza el cuestionario con el nombre de la empresa. El resto lo llenas tú en la entrevista o se lo mandas al cliente.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={crear} className="space-y-4">
            <div>
              <Label htmlFor="lev-empresa">Nombre de la empresa *</Label>
              <Input
                id="lev-empresa"
                autoFocus
                value={form.empresaNombre}
                onChange={(e) => setForm((f) => ({ ...f, empresaNombre: e.target.value }))}
                placeholder="Ej. Restaurante El Buen Sabor"
              />
            </div>
            <div>
              <Label htmlFor="lev-correo">Correo del contacto (opcional)</Label>
              <Input
                id="lev-correo"
                type="email"
                value={form.clienteEmail}
                onChange={(e) => setForm((f) => ({ ...f, clienteEmail: e.target.value }))}
                placeholder="Para enviarle el cuestionario más adelante"
              />
            </div>
            <div>
              <Label>País de la empresa (opcional)</Label>
              <Select value={form.pais || undefined} onValueChange={(v) => setForm((f) => ({ ...f, pais: v }))}>
                <SelectTrigger><SelectValue placeholder={paises === null ? 'Cargando países…' : 'Elige el país…'} /></SelectTrigger>
                <SelectContent>
                  {(paises || []).map((x) => (
                    <SelectItem key={x.code} value={x.code}>{x.nombre} ({x.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-caption text-subtle">
                Si ya lo sabes, pasa a la cotización. Es obligatorio para crear la empresa y no se asume ninguno.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)} disabled={creando}>
                Cancelar
              </Button>
              <Button type="submit" disabled={creando}>
                {creando ? 'Creando…' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
